import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Source, Layer, useMap } from 'react-map-gl';
import { formatInTimeZone } from 'date-fns-tz';
import debounce from 'lodash/debounce';
import './FOVLayer.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

// Utility function for formatting dates in UTC
const formatDateUTC = (date) => {
    return formatInTimeZone(date, 'UTC', 'yyyy-MM-dd');
};

const FieldOfViewLayer = ({ layerId, visible, currentDateTime, isPlaying }) => {
    console.log('FieldOfViewLayer render - visible:', visible, 'layerId:', layerId);
    
    const [geoJsonFeatures, setGeoJsonFeatures] = useState([]);
    const { current: map } = useMap();
    const [isLoading, setIsLoading] = useState(false);
    const [lastResponse, setLastResponse] = useState(null);
    const requestCancelRef = useRef(false);
    const prevDateRef = useRef(null);
    
    // Debug: Log map on initial render
    useEffect(() => {
        // console.log('Map object available:', !!map);
        if (map) {
            // console.log('Map style loaded:', !!map.getStyle);
            
            // Check if map is fully loaded before inspecting style
            if (map.isStyleLoaded()) {
                try {
                    const style = map.getStyle();
                    console.log('Map sources:', Object.keys(style.sources));
                    console.log('Map layers:', style.layers.map(l => l.id));
                } catch (e) {
                    console.error('Error inspecting map style:', e);
                }
            } else {
                console.log('Map style not yet loaded, waiting...');
                
                // Add a listener for the style.load event
                const onStyleLoad = () => {
                    try {
                        const style = map.getStyle();
                        // console.log('Map style loaded, sources:', Object.keys(style.sources));
                        // console.log('Map style loaded, layers:', style.layers.map(l => l.id));
                    } catch (e) {
                        console.error('Error inspecting map style after load:', e);
                    }
                };
                
                map.once('style.load', onStyleLoad);
                return () => {
                    map.off('style.load', onStyleLoad);
                };
            }
        }
    }, [map]);
    
    // Use a simplified flight level state with FL320 active
    // This requests filtered data (~9MB) instead of all levels (23MB)
    const [flightLevelVisibility, setFlightLevelVisibility] = useState({
        FL320: true
    });

    // Get active flight levels as a comma-separated string for API calls
    const activeFlightLevels = useMemo(() => {
        const levels = Object.entries(flightLevelVisibility)
            .filter(([_, isVisible]) => isVisible)
            .map(([level]) => level)
            .join(',');
        // console.log('Active flight levels:', levels);
        return levels;
    }, [flightLevelVisibility]);

    // Function to directly test the backend endpoint
    const testBackendEndpoint = useCallback(async () => {
        setIsLoading(true);
        const currentDateStr = formatDateUTC(currentDateTime);
        // console.log('Testing backend with date:', currentDateStr);

        try {
            // Request with flight level filter to get smaller payload
            let url = `${API_BASE_URL}/fov_geojson?date_str=${currentDateStr}`;
            if (activeFlightLevels) {
                url += `&flight_levels=${encodeURIComponent(activeFlightLevels)}`;
            }
            console.log('Testing URL:', url);
            
            const response = await fetch(url);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            console.log('Backend test response:', data);
            console.log('Feature count:', data.features ? data.features.length : 0);
            
            if (data.features && data.features.length > 0) {
                // console.log('Found features! Setting state with', data.features.length, 'features');
                setGeoJsonFeatures(data.features);
                
                // Update flight levels from data
                const newFlightLevels = {};
                data.features.forEach(feature => {
                    if (feature.properties && feature.properties.flightLevel) {
                        newFlightLevels[feature.properties.flightLevel] = false;
                    }
                });

                // Always keep FL320 active if it exists
                if (newFlightLevels.FL320 !== undefined) {
                    newFlightLevels.FL320 = true;
                }

                // console.log('Setting flight levels:', newFlightLevels);
                setFlightLevelVisibility(prev => ({...prev, ...newFlightLevels}));
            } else {
                console.warn('No features in response');
            }
            
            setLastResponse(data);
        } catch (error) {
            console.error('Error testing backend:', error);
        } finally {
            setIsLoading(false);
        }
    }, [currentDateTime, activeFlightLevels]);

    // Fetch GeoJSON data from the backend (pre-processed)
    const fetchGeoJSON = useCallback(async (dateStr, flightLevels) => {
        if (!visible) {
            console.log('Skipping fetch - layer not visible');
            return;
        }
        
        // Skip if date hasn't changed and we already have data
        if (prevDateRef.current === dateStr && geoJsonFeatures.length > 0) {
            // console.log('Skipping fetch - using cached data for', dateStr);
            return;
        }
        
        setIsLoading(true);
        requestCancelRef.current = false;
        prevDateRef.current = dateStr;
        
        console.log('Fetching GeoJSON for date:', dateStr, 'flight levels:', flightLevels);
        
        try {
            // Add flight levels to the query if we have any selected
            let url = `${API_BASE_URL}/fov_geojson?date_str=${dateStr}`;
            if (flightLevels) {
                url += `&flight_levels=${encodeURIComponent(flightLevels)}`;
            }
            
            console.log('Fetching from URL:', url);
            const response = await fetch(url);
            
            // If another request was made, cancel this one
            if (requestCancelRef.current) {
                console.log('Request canceled - newer request is in progress');
                return;
            }
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            setLastResponse(data); // Store the last response for debugging
            
            console.log('GeoJSON response received:', data);
            console.log('Feature count:', data.features ? data.features.length : 0);
            
            if (data.features && data.features.length > 0) {
                // console.log('First feature sample:', JSON.stringify(data.features[0]).substring(0, 200) + '...');
                // console.log('First feature properties:', data.features[0].properties);
                
                // Set the features directly
                // console.log('Setting features state with count:', data.features.length);
                setGeoJsonFeatures(data.features);
                
                // Update available flight levels from received data
                const newFlightLevels = {};
                data.features.forEach(feature => {
                    const flightLevel = feature.properties?.flightLevel;
                    if (flightLevel) {
                        // Only enable FL320 by default
                        newFlightLevels[flightLevel] = flightLevel === 'FL320';
                    }
                });

                // console.log('Detected flight levels in data:', Object.keys(newFlightLevels));

                // Update flight level visibility with new levels
                if (Object.keys(newFlightLevels).length > 0) {
                    setFlightLevelVisibility(prev => ({...prev, ...newFlightLevels}));
                }
            } else {
                console.warn('No features in GeoJSON response');
                setGeoJsonFeatures([]);
            }
        } catch (error) {
            console.error('Error fetching GeoJSON data:', error);
        } finally {
            setIsLoading(false);
        }
    }, [visible]);

    // Debounce the fetch for non-playing mode
    const debouncedFetchGeoJSON = useMemo(
        () => debounce(fetchGeoJSON, 300),
        [fetchGeoJSON]
    );
    
    // Effect to fetch data when relevant props change
    useEffect(() => {
        console.log('Data fetch effect triggered - visible:', visible);
        if (!visible) return;
        
        const currentDateStr = formatDateUTC(currentDateTime);
        console.log('Formatted date for fetch:', currentDateStr);
        
        // Cancel any pending requests
        requestCancelRef.current = true;
        
        // Start a new request
        if (isPlaying) {
            console.log('Fetching immediately (playing mode)');
            fetchGeoJSON(currentDateStr, activeFlightLevels);
        } else {
            console.log('Fetching with debounce (not playing)');
            debouncedFetchGeoJSON(currentDateStr, activeFlightLevels);
        }
        
        return () => {
            console.log('Cleanup for data fetch effect');
            requestCancelRef.current = true;
            debouncedFetchGeoJSON.cancel();
        };
    }, [
        visible, 
        currentDateTime, 
        isPlaying, 
        fetchGeoJSON, 
        debouncedFetchGeoJSON, 
        activeFlightLevels
    ]);
    
    // Memoize the flight levels for rendering in the UI
    const flightLevelsList = useMemo(() => 
        Object.keys(flightLevelVisibility).sort(), 
        [flightLevelVisibility]
    );
    
    // Create GeoJSON data with all features
    const geoJsonData = useMemo(() => {
        // Filter based on flight level visibility
        const filteredFeatures = geoJsonFeatures.filter(
            feature => feature.properties?.flightLevel && 
            flightLevelVisibility[feature.properties.flightLevel]
        );
        
        const data = {
            type: 'FeatureCollection',
            features: filteredFeatures,
        };
        console.log('Created GeoJSON data with filtered features count:', filteredFeatures.length);
        return data;
    }, [geoJsonFeatures, flightLevelVisibility]);
    
    // Flight level toggle handler
    const toggleFlightLevelVisibility = useCallback((flightLevel) => {
        console.log('Toggling flight level:', flightLevel);
        setFlightLevelVisibility(prev => ({
            ...prev, 
            [flightLevel]: !prev[flightLevel]
        }));
    }, []);
    
    // Debug: Check if Source and Layer are being added to the map
    useEffect(() => {
        if (!map || !visible) return;
        
        const sourceId = `${layerId}-fov`;
        const fillLayerId = `${layerId}-fov-fill`;
        const outlineLayerId = `${layerId}-fov-outline`;
        
        // Wait for next tick to check if our layers were added
        setTimeout(() => {
            try {
                const style = map.getStyle();
                const hasSource = !!style.sources[sourceId];
                const hasFillLayer = style.layers.some(l => l.id === fillLayerId);
                const hasOutlineLayer = style.layers.some(l => l.id === outlineLayerId);
                
                console.log('Map source/layer check:');
                console.log('- Source exists:', hasSource);
                console.log('- Fill layer exists:', hasFillLayer);
                console.log('- Outline layer exists:', hasOutlineLayer);
            } catch (e) {
                console.error('Error checking map layers:', e);
            }
        }, 1000);
    }, [map, visible, layerId, geoJsonData]);
    
    // Don't render anything if the layer isn't visible
    if (!visible) {
        console.log('Layer not visible, rendering null');
        return null;
    }
    
    console.log('Rendering FOV layer with', geoJsonFeatures.length, 'total features');
    
    return (
        <>
            <div className="flight-level-selector">
                {flightLevelsList.map((flightLevel) => (
                    <label key={flightLevel} style={{ marginRight: '10px' }}>
                        <input
                            type="checkbox"
                            checked={flightLevelVisibility[flightLevel]}
                            onChange={() => toggleFlightLevelVisibility(flightLevel)}
                        />
                        {flightLevel}
                    </label>
                ))}
                {isLoading && <span className="loading-indicator">Loading...</span>}
                <span style={{ marginLeft: '10px' }}>
                    Features: {geoJsonFeatures.length}
                </span>
                
                {/* Debug button to force a fetch */}
                <button 
                    onClick={() => {
                        console.log('Manual fetch triggered');
                        prevDateRef.current = null; // Reset cache check
                        const currentDateStr = formatDateUTC(currentDateTime);
                        fetchGeoJSON(currentDateStr, activeFlightLevels);
                    }}
                    style={{ marginLeft: '10px' }}
                >
                    Force Refresh
                </button>
                
                {/* Debug button to test backend directly */}
                <button 
                    onClick={testBackendEndpoint}
                    style={{ marginLeft: '5px' }}
                >
                    Test Backend
                </button>
                
                {/* Debug button to dump last response */}
                <button 
                    onClick={() => {
                        console.log('Last GeoJSON response:', lastResponse);
                        console.log('Current geoJsonFeatures:', geoJsonFeatures);
                    }}
                    style={{ marginLeft: '5px' }}
                >
                    Dump Data
                </button>
            </div>

            {/* Show debug info about the GeoJSON data */}
            <div style={{ 
                position: 'absolute', 
                top: '10px', 
                right: '10px', 
                background: 'rgba(255,255,255,0.8)', 
                padding: '8px',
                borderRadius: '4px',
                fontSize: '12px',
                display: geoJsonFeatures.length ? 'block' : 'none'
            }}>
                Features: {geoJsonFeatures.length}<br/>
                Active Levels: {activeFlightLevels || 'none'}
            </div>

            <Source
                id={`${layerId}-fov`}
                type="geojson"
                data={geoJsonData}
            >
                <Layer
                    id={`${layerId}-fov-fill`}
                    type="fill"
                    paint={{
                        'fill-color': 'rgba(0, 255, 255, 0.5)',
                        'fill-outline-color': 'rgba(0, 255, 255, 1)',
                        'fill-opacity': 0.3,
                    }}
                />
                <Layer
                    id={`${layerId}-fov-outline`}
                    type="line"
                    paint={{
                        'line-color': 'rgba(0, 255, 255, 1)',
                        'line-width': 1,
                    }}
                />
            </Source>
        </>
    );
};

export default React.memo(FieldOfViewLayer);